import { type ChangeEvent, useRef, useState } from "react";

import { AskButton } from "@doitintl/ava-components/src/Messenger/AskButton";
import { StopGenerationButton } from "@doitintl/ava-components/src/Messenger/StopGenerationButton";
import { Box, Stack, TextField, type TextFieldProps } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { FileAttachment } from "./FileAttachment";
import { useConversationIdChanged } from "./hooks";

type Props = {
  disableInput: boolean;
  onAskQuestionClicked: (question: string, files?: File[]) => Promise<void>;
  showStopButton: boolean;
  showGeneratingIndicator?: boolean;
  handleStopGeneration: () => void;
  sx?: SxProps<Theme>;
  textFieldSx?: SxProps<Theme>;
  textFieldProps?: TextFieldProps;
  iconWrapperSx?: SxProps<Theme>;
  resetQuestion?: boolean;
  showFileAttachment?: boolean;
  fileAttachmentSx?: SxProps<Theme>;
};

export const MessengerFooter = ({
  disableInput,
  onAskQuestionClicked,
  showStopButton,
  showGeneratingIndicator,
  handleStopGeneration,
  sx = {},
  textFieldSx = {},
  textFieldProps = {},
  resetQuestion = true,
  showFileAttachment = true,
  iconWrapperSx = {},
  fileAttachmentSx = {},
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [question, setQuestion] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  useConversationIdChanged(() => {
    ref.current?.focus();
  });

  const handleAskQuestion = async () => {
    if (question.trim().length === 0) {
      return;
    }

    onAskQuestionClicked(question.trim(), selectedFiles);

    if (resetQuestion) {
      setQuestion("");
    }
    setSelectedFiles([]);
  };

  return (
    <Stack
      sx={{
        backgroundColor: "background.paper",
        px: 2,
        py: 1.5,
        borderTopWidth: 1,
        borderTopColor: "divider",
        borderTopStyle: "solid",
        mt: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "relative",
          maxWidth: 800,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <TextField
          inputRef={ref}
          onCompositionStart={() => {
            setIsComposing(true);
          }}
          onCompositionEnd={() => {
            setIsComposing(false);
          }}
          fullWidth
          multiline
          autoFocus
          maxRows={5}
          placeholder="Ask Ava"
          value={question}
          disabled={disableInput && !showStopButton}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && question && !isComposing) {
              if (showStopButton) {
                event.preventDefault();
                handleStopGeneration();
                return;
              }
              return handleAskQuestion();
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              pb: 6,
            },
            "& .MuiOutlinedInput-input": {
              py: 1.5,
              pb: 1.5,
              pl: 0.5,
            },
            ...textFieldSx,
          }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            if (!showStopButton) {
              setQuestion(event.target.value);
            } else if (resetQuestion) {
              setQuestion("");
            }
          }}
          data-cy="ask-question-input"
          variant="outlined"
          {...textFieldProps}
        />
        {showFileAttachment && (
          <FileAttachment
            disabled={disableInput && !showStopButton}
            onFilesChange={setSelectedFiles}
            files={selectedFiles}
            sx={fileAttachmentSx}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            ...iconWrapperSx,
          }}
        >
          {!showStopButton ? (
            <AskButton
              handleAskQuestion={handleAskQuestion}
              question={question}
              processingQuestion={disableInput}
              showGeneratingIndicator={showGeneratingIndicator}
            />
          ) : (
            <StopGenerationButton handleStopGeneration={handleStopGeneration} />
          )}
        </Box>
      </Box>
    </Stack>
  );
};
